import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { doc, getDoc, collection, getDocs, query, where, orderBy, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { Cloudinary } from '@cloudinary/url-gen';
import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET } from '../utils/cloudinaryConfig';
import useResponsive from './Map/ResponsiveLayout'; // Ajout du hook useResponsive
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  FormHelperText
} from '@mui/material';
import {
  ShoppingBag,
  User,
  Package,
  LogOut,
  Plus,
  Edit,
  Trash,
  AlertCircle,
  Upload,
  X
} from 'lucide-react';
import { notifyAdmins } from '../services/notificationService';

const FarmerDashboard = () => {
  const navigate = useNavigate();
  const isMobile = useResponsive(); // Utilisation du hook pour détecter si on est sur mobile
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  
  // États pour le formulaire d'annonce
  const [openAnnouncementDialog, setOpenAnnouncementDialog] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    description: '',
    price: '',
    quantity: '',
    unit: 'kg',
    category: '',
    imageUrls: [] // Ajout du tableau d'URLs d'images
  });
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [imageUploading, setImageUploading] = useState(false); // État pour suivre le chargement des images

  // Initialisation de Cloudinary
  const cld = new Cloudinary({
    cloud: {
      cloudName: CLOUDINARY_CLOUD_NAME
    }
  });

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    try {
      setImageUploading(true);
      
      // Créer un objet FormData pour l'upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('cloud_name', CLOUDINARY_CLOUD_NAME);
      
      // Upload vers Cloudinary
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erreur Cloudinary:', errorData);
        throw new Error(`Erreur d'upload: ${errorData.error?.message || 'Problème de connexion à Cloudinary'}`);
      }
      
      const data = await response.json();
      
      if (data.secure_url) {
        // Limiter à 3 images maximum
        setAnnouncementForm(prev => {
          const newImageUrls = [...prev.imageUrls];
          if (newImageUrls.length < 3) {
            newImageUrls.push(data.secure_url);
          } else {
            setError('Maximum 3 images par annonce');
          }
          return {
            ...prev,
            imageUrls: newImageUrls
          };
        });
        setSuccess('Image téléchargée avec succès');
      } else {
        throw new Error('URL sécurisée non reçue de Cloudinary');
      }
    } catch (error) {
      console.error('Erreur lors du téléchargement de l\'image:', error);
      setError(`Erreur lors du téléchargement de l'image: ${error.message}`);
    } finally {
      setImageUploading(false);
    }
  };

  const removeImage = (indexToRemove) => {
    setAnnouncementForm(prev => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, index) => index !== indexToRemove)
    }));
  };

  useEffect(() => {
    // Appliquer la couleur de fond pour les farmers
    document.body.style.backgroundColor = '#f8f9fa';
    
    const fetchUserData = async () => {
      try {
        const user = auth.currentUser;
        
        if (!user) {
          navigate('/farmer-login');
          return;
        }

        const userDoc = await getDoc(doc(db, 'farmers', user.uid));
        
        if (!userDoc.exists()) {
          setError('Données utilisateur non trouvées');
          return;
        }
        
        setUserData(userDoc.data());
        fetchAnnouncements(user.uid);
      } catch (err) {
        console.error('Erreur lors de la récupération des données:', err);
        setError('Erreur lors de la récupération des données');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    
    // Nettoyer la couleur de fond lors du démontage du composant
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, [navigate]);

  const fetchAnnouncements = async (userId) => {
    try {
      const announcementsQuery = query(
        collection(db, 'announcements'),
        where('farmerId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const announcementsSnapshot = await getDocs(announcementsQuery);
      const announcementsList = announcementsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      
      setAnnouncements(announcementsList);
    } catch (err) {
      console.error('Erreur lors de la récupération des annonces:', err);
      setError('Erreur lors de la récupération des annonces');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAnnouncementChange = (e) => {
    const { name, value } = e.target;
    setAnnouncementForm({
      ...announcementForm,
      [name]: value
    });
  };

  const handleOpenAnnouncementDialog = (announcement = null) => {
    if (announcement) {
      setEditingAnnouncement(announcement.id);
      setAnnouncementForm({
        title: announcement.title,
        description: announcement.description,
        price: announcement.price,
        quantity: announcement.quantity,
        unit: announcement.unit || 'kg',
        category: announcement.category || '',
        imageUrls: announcement.imageUrls || [] // Initialisation du tableau d'URLs d'images
      });
    } else {
      setEditingAnnouncement(null);
      setAnnouncementForm({
        title: '',
        description: '',
        price: '',
        quantity: '',
        unit: 'kg',
        category: '',
        imageUrls: [] // Réinitialisation du tableau d'URLs d'images
      });
    }
    setOpenAnnouncementDialog(true);
  };

  const handleCloseAnnouncementDialog = () => {
    setOpenAnnouncementDialog(false);
  };

  // Fonction de soumission d'annonce
  const handleSubmitAnnouncement = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      
      if (!user) {
        setError('Utilisateur non connecté');
        setLoading(false);
        return;
      }
      
      const announcementData = {
        ...announcementForm,
        price: parseFloat(announcementForm.price) || 0,
        quantity: parseFloat(announcementForm.quantity) || 0,
        farmerId: user.uid,
        farmerName: userData.name,
        farmName: userData.farmName,
        location: userData.location,
        status: 'pending',
        updatedAt: serverTimestamp(),
        imageUrls: announcementForm.imageUrls || [] // Ajout du tableau d'URLs d'images
      };
      
      let announcementId;
      
      if (editingAnnouncement) {
        // Mise à jour d'une annonce existante
        await updateDoc(doc(db, 'announcements', editingAnnouncement), {
          ...announcementData,
          updatedAt: serverTimestamp()
        });
        setSuccess('Annonce mise à jour avec succès');
        announcementId = editingAnnouncement;
        
        // Notifier les administrateurs de la modification
        await notifyAdmins(announcementId, announcementData.title, 'updated', userData.name);
      } else {
        // Création d'une nouvelle annonce
        announcementData.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, 'announcements'), announcementData);
        setSuccess('Annonce créée avec succès');
        announcementId = docRef.id;
        
        // Notifier les administrateurs de la création
        await notifyAdmins(announcementId, announcementData.title, 'created', userData.name);
      }
      
      handleCloseAnnouncementDialog();
      fetchAnnouncements(user.uid);
    } catch (err) {
      console.error('Erreur lors de la soumission de l\'annonce:', err);
      setError('Erreur lors de la soumission de l\'annonce');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) {
      try {
        setLoading(true);
        await deleteDoc(doc(db, 'announcements', id));
        setSuccess('Annonce supprimée avec succès');
        fetchAnnouncements(auth.currentUser.uid);
      } catch (err) {
        console.error('Erreur lors de la suppression de l\'annonce:', err);
        setError('Erreur lors de la suppression de l\'annonce');
      } finally {
        setLoading(false);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Approuvée';
      case 'pending':
        return 'En attente';
      case 'rejected':
        return 'Rejetée';
      default:
        return 'Inconnu';
    }
  };

  if (loading && !userData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Profil de l'agriculteur */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: '#014527',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  fontSize: '2rem'
                }}
              >
                {userData?.name?.charAt(0) || 'A'}
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {userData?.name || 'Agriculteur'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userData?.email || ''}
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Informations de la ferme
              </Typography>
              <Typography variant="body2">
                <strong>Nom:</strong> {userData?.farmName || 'Non spécifié'}
              </Typography>
              <Typography variant="body2">
                <strong>Localisation:</strong> {userData?.location || 'Non spécifiée'}
              </Typography>
              <Typography variant="body2">
                <strong>Cultures:</strong> {userData?.crops || 'Non spécifiées'}
              </Typography>
              <Typography variant="body2">
                <strong>Statut:</strong> {' '}
                <Chip 
                  size="small" 
                  label={userData?.status === 'approved' ? 'Approuvé' : 'En attente'} 
                  color={userData?.status === 'approved' ? 'success' : 'warning'}
                />
              </Typography>
            </Box>
            
            <Button
              variant="outlined"
              color="error"
              fullWidth
              startIcon={<LogOut size={18} />}
              onClick={handleLogout}
              sx={{ mt: 3 }}
            >
              Se déconnecter
            </Button>
          </Paper>
        </Grid>
        
        {/* Contenu principal */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: '10px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label="Tableau de bord" />
                <Tab label="Mes annonces" />
              </Tabs>
            </Box>
            
            {/* Tableau de bord */}
            {activeTab === 0 && (
              <Box>
                <Typography variant="h5" sx={{ mb: 3, color: '#014527', fontWeight: 'bold' }}>
                  Tableau de bord
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Card sx={{ 
                      height: '100%', 
                      borderRadius: '10px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                      transition: 'transform 0.3s',
                      '&:hover': { transform: 'translateY(-5px)' }
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <ShoppingBag size={24} color="#014527" style={{ marginRight: '8px' }} />
                          <Typography variant="h6">Mes annonces</Typography>
                        </Box>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#014527' }}>
                          {announcements.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total des annonces créées
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Card sx={{ 
                      height: '100%', 
                      borderRadius: '10px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                      transition: 'transform 0.3s',
                      '&:hover': { transform: 'translateY(-5px)' }
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <AlertCircle size={24} color="#f59e0b" style={{ marginRight: '8px' }} />
                          <Typography variant="h6">En attente</Typography>
                        </Box>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                          {announcements.filter(a => a.status === 'pending').length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Annonces en attente de validation
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Dernières annonces
                  </Typography>
                  
                  {announcements.length > 0 ? (
                    <>
                      {/* Affichage tableau pour desktop */}
                      <TableContainer 
                        component={Paper} 
                        sx={{ 
                          boxShadow: 'none', 
                          border: '1px solid #eee',
                          display: { xs: 'none', md: 'block' } // Masquer sur mobile
                        }}
                      >
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Titre</TableCell>
                              <TableCell>Prix</TableCell>
                              <TableCell>Statut</TableCell>
                              <TableCell>Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {announcements.slice(0, 5).map((announcement) => (
                              <TableRow key={announcement.id}>
                                <TableCell>{announcement.title}</TableCell>
                                <TableCell>{announcement.price} FCFA/{announcement.unit}</TableCell>
                                <TableCell>
                                  <Chip 
                                    size="small" 
                                    label={getStatusText(announcement.status)} 
                                    color={getStatusColor(announcement.status)}
                                  />
                                </TableCell>
                                <TableCell>
                                  {announcement.createdAt.toLocaleDateString()}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {/* Affichage en cartes pour mobile */}
                      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
                        {announcements.slice(0, 5).map((announcement) => (
                          <Paper 
                            key={announcement.id} 
                            elevation={1} 
                            sx={{ p: 2, borderRadius: '8px' }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {announcement.title}
                              </Typography>
                              <Chip 
                                size="small" 
                                label={getStatusText(announcement.status)} 
                                color={getStatusColor(announcement.status)}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                              <Typography variant="body2">
                                <strong>Prix:</strong> {announcement.price} FCFA/{announcement.unit}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {announcement.createdAt.toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Vous n'avez pas encore créé d'annonces.
                    </Typography>
                  )}
                  
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Plus size={18} />}
                    onClick={() => handleOpenAnnouncementDialog()}
                    sx={{ mt: 3, bgcolor: '#014527' }}
                  >
                    Créer une annonce
                  </Button>
                </Box>
              </Box>
            )}
            
            {/* Mes annonces */}
            {activeTab === 1 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ color: '#014527', fontWeight: 'bold' }}>
                    Mes annonces
                  </Typography>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Plus size={18} />}
                    onClick={() => handleOpenAnnouncementDialog()}
                    sx={{ bgcolor: '#014527' }}
                  >
                    Créer une annonce
                  </Button>
                </Box>
                
                {announcements.length > 0 ? (
                  <>
                    {/* Affichage tableau pour desktop */}
                    <TableContainer 
                      component={Paper} 
                      sx={{ 
                        boxShadow: 'none', 
                        border: '1px solid #eee',
                        display: { xs: 'none', md: 'block' } // Masquer sur mobile
                      }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Titre</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Prix</TableCell>
                            <TableCell>Quantité</TableCell>
                            <TableCell>Statut</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {announcements.map((announcement) => (
                            <TableRow key={announcement.id}>
                              <TableCell>{announcement.title}</TableCell>
                              <TableCell>
                                {announcement.description.length > 50
                                  ? `${announcement.description.substring(0, 50)}...`
                                  : announcement.description}
                              </TableCell>
                              <TableCell>{announcement.price} FCFA/{announcement.unit}</TableCell>
                              <TableCell>{announcement.quantity} {announcement.unit}</TableCell>
                              <TableCell>
                                <Chip 
                                  size="small" 
                                  label={getStatusText(announcement.status)} 
                                  color={getStatusColor(announcement.status)}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleOpenAnnouncementDialog(announcement)}
                                    startIcon={<Edit size={18} />}
                                    sx={{ 
                                      flex: 2,
                                      bgcolor: '#014527',
                                      '&:hover': { bgcolor: '#013a20' }
                                    }}
                                  >
                                    Modifier
                                  </Button>
                                  <IconButton
                                    color="error"
                                    onClick={() => handleDeleteAnnouncement(announcement.id)}
                                    sx={{ flex: 1 }}
                                  >
                                    <Trash size={20} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* Affichage en cartes pour mobile */}
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
                      {announcements.map((announcement) => (
                        <Paper 
                          key={announcement.id} 
                          elevation={1} 
                          sx={{ p: 2, borderRadius: '8px' }}
                        >
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              {announcement.title}
                            </Typography>
                            <Chip 
                              size="small" 
                              label={getStatusText(announcement.status)} 
                              color={getStatusColor(announcement.status)}
                              sx={{ mb: 1 }}
                            />
                          </Box>
                          
                          {announcement.imageUrls && announcement.imageUrls.length > 0 && (
                            <Box sx={{ mb: 2, width: '100%', height: 150, overflow: 'hidden', borderRadius: '4px' }}>
                              <img 
                                src={announcement.imageUrls[0]} 
                                alt={announcement.title}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                              />
                            </Box>
                          )}
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {announcement.description.length > 100
                              ? `${announcement.description.substring(0, 100)}...`
                              : announcement.description}
                          </Typography>
                          
                          <Grid container spacing={1} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                              <Typography variant="body2">
                                <strong>Prix:</strong> {announcement.price} FCFA/{announcement.unit}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2">
                                <strong>Quantité:</strong> {announcement.quantity} {announcement.unit}
                              </Typography>
                            </Grid>
                          </Grid>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleOpenAnnouncementDialog(announcement)}
                              startIcon={<Edit size={18} />}
                              sx={{ 
                                flex: 2,
                                bgcolor: '#014527',
                                '&:hover': { bgcolor: '#013a20' }
                              }}
                            >
                              Modifier
                            </Button>
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteAnnouncement(announcement.id)}
                              sx={{ flex: 1 }}
                            >
                              <Trash size={20} />
                            </IconButton>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Vous n'avez pas encore créé d'annonces.
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* Dialogue pour créer/modifier une annonce */}
      <Dialog open={openAnnouncementDialog} onClose={handleCloseAnnouncementDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAnnouncement ? 'Modifier l\'annonce' : 'Créer une nouvelle annonce'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label="Titre de l'annonce"
            type="text"
            fullWidth
            variant="outlined"
            value={announcementForm.title}
            onChange={handleAnnouncementChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            value={announcementForm.description}
            onChange={handleAnnouncementChange}
            required
            multiline
            rows={4}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="price"
                label="Prix (FCFA)"
                type="number"
                fullWidth
                variant="outlined"
                value={announcementForm.price}
                onChange={handleAnnouncementChange}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="quantity"
                label="Quantité"
                type="number"
                fullWidth
                variant="outlined"
                value={announcementForm.quantity}
                onChange={handleAnnouncementChange}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                margin="dense"
                name="unit"
                label="Unité"
                fullWidth
                variant="outlined"
                value={announcementForm.unit}
                onChange={handleAnnouncementChange}
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value="kg">Kilogramme (kg)</option>
                <option value="g">Gramme (g)</option>
                <option value="l">Litre (l)</option>
                <option value="unité">Unité</option>
                <option value="sac">Sac</option>
                <option value="caisse">Caisse</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                margin="dense"
                name="category"
                label="Catégorie"
                fullWidth
                variant="outlined"
                value={announcementForm.category}
                onChange={handleAnnouncementChange}
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Sélectionner une catégorie</option>
                <option value="legumes">Légumes</option>
                <option value="fruits">Fruits</option>
                <option value="cereales">Céréales</option>
                <option value="viande">Viande</option>
                <option value="poisson">Poisson</option>
                <option value="produits_laitiers">Produits laitiers</option>
                <option value="autre">Autre</option>
              </TextField>
            </Grid>
          </Grid>
          
          {/* Ajout du composant d'upload d'images */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Images du produit (max 3)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Affichage des images déjà téléchargées */}
              {announcementForm.imageUrls.length > 0 && (
                <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  {announcementForm.imageUrls.map((url, index) => (
                    <Box key={index} sx={{ position: 'relative', width: 100, height: 100 }}>
                      <img 
                        src={url} 
                        alt={`Image ${index + 1}`} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }} 
                      />
                      <IconButton 
                        size="small" 
                        sx={{ position: 'absolute', top: -10, right: -10, bgcolor: 'rgba(255,255,255,0.8)' }}
                        onClick={() => removeImage(index)}
                      >
                        <X size={16} />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
              
              {/* Bouton d'upload */}
              {announcementForm.imageUrls.length < 3 && (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                  disabled={imageUploading}
                >
                  {imageUploading ? 'Téléchargement...' : 'Ajouter une image'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={uploadImage}
                  />
                </Button>
              )}
              <FormHelperText>Formats acceptés: JPG, PNG. Taille max: 5MB</FormHelperText>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAnnouncementDialog} color="inherit">
            Annuler
          </Button>
          <Button 
            onClick={handleSubmitAnnouncement} 
            variant="contained" 
            color="primary"
            disabled={!announcementForm.title || !announcementForm.description || !announcementForm.price || !announcementForm.quantity || !announcementForm.category}
            sx={{ bgcolor: '#014527' }}
          >
            {editingAnnouncement ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FarmerDashboard;